import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiTrash } from "react-icons/bi";
import { CiUnlock } from "react-icons/ci";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useAppSelector } from "../../app/hook";
import {
  useGetPackageQuery,
  useUpdateWidgetSettingMutation
} from "../../app/services/chatbot-rtk.services";
import PopoverWithAudio, {
  IPopoverWithAudioType
} from "../../components/common/PopoverWithAudio";
import ApiKeyConfiguration from "../../components/dashboard/ApiKeyConfiguration";
import ProBadge from "../../components/ui/ProBadge";
import { selectWidgetCustomize } from "../../features/dashboard.slice";
import usePermission from "../../hooks/usePermission";

const AutoInstallAudioLinks: any = {
  en: "https://cdn.1cdn.app/application/LOUISEBOT/24021113585646_pop_en.mp3",
  fr: "https://cdn.1cdn.app/application/LOUISEBOT/24021113585648_pop_fr.mp3"
};

type SetDomainType = {
  autoInstall: boolean;
  domains: { url: string }[];
  // workOnDesktop: boolean;
  // poweredBy: boolean;
};

const SettingTempPage = () => {
  const { t, i18n } = useTranslation();

  const permissions = usePermission();

  const widget = useAppSelector(selectWidgetCustomize);

  const getClient = useGetPackageQuery();

  const [updateGeneralSettings, updateGeneralSettingsResults] =
    useUpdateWidgetSettingMutation();

  const schema = Yup.object({
    autoInstall: Yup.boolean().required().label("Auto-install"),
    domains: Yup.array(
      Yup.object({
        url: Yup.string()
          /**
           * The maximum character limit for the last part of a domain (also known as the Top-Level Domain or TLD)  depends on the context:
           * Technically: Each label (including the TLD) in a domain name can have a maximum of 63 characters. This limit is defined by the underlying technical specifications of the Domain Name System (DNS).
           * Practically: Most domain registries enforce a stricter limit of 63 characters for the entire domain name, including all labels and the periods (dots) separating them. This ensures compatibility with various applications and software that might have shorter limits.
           * Therefore, it's best to keep the entire domain name, including the TLD, under 63 characters for guaranteed functionality.
           */
          .matches(
            /^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z0-9()]{1,15}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/i,
            {
              message: t("DashboardSettingWidgetDefaultDomain/er-01")
            }
          )
          .required()
      })
    )

      .required()
      .label("Domains")
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    control,
    reset
  } = useForm<SetDomainType>({
    defaultValues: {
      autoInstall: false,
      domains: [{ url: "" }]
    },
    resolver: yupResolver(schema),
    mode: "onSubmit"
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "domains"
  });

  const onSubmit = (data: SetDomainType) => {
    updateGeneralSettings({
      ...data,
      domains: [...data.domains.map((domain) => `https://` + domain.url)]
    })
      .unwrap()
      .then((res) => {
        toast.success("Successfully updated");
      })
      .catch((err) => {
        toast.error(err.data.message || err.message);
      });
  };

  const AutoInstallPopData: IPopoverWithAudioType = {
    audioUrl: AutoInstallAudioLinks[i18n.language],
    contentText: t("DashboardSettingWidgetDefaultDomain/pop-01")
  };

  useEffect(() => {
    if (getClient.isSuccess)
      reset({
        autoInstall:
          getClient.data?.config.widget_defaults.autoInstall ?? false,
        domains: (getClient.data?.config.widget_defaults.domains ?? []).map(
          (domain: string) => ({
            url: domain.replace("https://", "")
          })
        )
      });
  }, [getClient.isFetching, getClient.isSuccess]);

  return (
    <section className="setting-page tw-p-5 tw-flex tw-flex-col tw-gap-4">
      <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
        <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
          <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
            {t("DashboardSettingWidgetDefaultDomain/title")}
          </div>
          <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t("DashboardSettingWidgetDefaultDomain/des-01")}
          </div>
        </div>

        <Form onSubmit={handleSubmit(onSubmit)} className="tw-w-full">
          {/* Auto install */}
          <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
            <Form.Check
              type="switch"
              id="autoInstall"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingWidgetDefaultDomain/l-01")}
              {...register("autoInstall")}
            />
            <PopoverWithAudio data={AutoInstallPopData} />
          </Form.Group>

          {/* Domains */}
          {fields.map((field, index) => (
            <div key={index} className="tw-mb-4">
              <div
                key={field.id}
                className="md:tw-w-[350px] tw-w-[85%] tw-flex tw-items-center tw-gap-2"
              >
                <InputGroup className="tw-grow tw-shrink-0">
                  <InputGroup.Text>https://</InputGroup.Text>
                  <Form.Control
                    type="text"
                    isInvalid={!!errors.domains?.[index]?.url}
                    {...register(`domains.${index}.url` as const)}
                  />
                </InputGroup>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="tw-bg-transparent tw-border-0 tw-ml-2"
                >
                  <BiTrash size={22} className="tw-text-gray-400" />
                </button>
              </div>
              <Form.Control.Feedback
                type="invalid"
                className={errors.domains?.[index]?.url ? `tw-inline-flex` : ""}
              >
                {errors?.domains?.[index]?.url?.message}
              </Form.Control.Feedback>
            </div>
          ))}

          {!!errors.domains && (
            <div className="tw-text-red-600 tw-my-4">
              {errors.domains.message}
            </div>
          )}

          <button
            className="tw-w-auto tw-px-[13px] tw-py-[9px] tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-solid tw-border-gray-300 tw-justify-center tw-items-center tw-inline-flex tw-text-gray-700 tw-text-sm tw-font-medium font-['Inter'] tw-leading-none tw-mb-8"
            type="button"
            onClick={() => append({ url: "" })}
          >
            <CiUnlock size={22} className="tw-text-gray-400 tw-mr-4" />
            {t("DashboardSettingWidgetDefaultDomain/add-domains")}
          </button>

          {/* <div className="tw-text-gray-500 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none">
            The HTML file you download would not work if the local use setting
            is disabled.
          </div> */}

          <hr className="tw-border-gray-400 tw-my-8" />

          <div className="tw-flex tw-justify-end">
            <button
              disabled={updateGeneralSettingsResults.isLoading || !isDirty}
              type="submit"
              className={`tw-px-[17px] tw-py-[9px] ${
                isDirty ? "tw-bg-primary" : "tw-bg-gray-400"
              } tw-border-0 tw-rounded-[99px] tw-shadow tw-justify-center tw-items-center tw-inline-flex tw-text-white tw-text-sm font-normal tw-font-['Lexend'] tw-leading-tight`}
            >
              {t("DashboardSettingWidgetDefaultDomain/apply")}
            </button>
          </div>
        </Form>
      </div>

      {!permissions.settingsPage.widget.customization.poweredBy && (
        <div className="tw-p-5 tw-bg-white tw-rounded-lg tw-shadow tw-flex-col tw-justify-start tw-items-start tw-gap-8 tw-flex">
          <div className="tw-w-full tw-flex-col tw-justify-start tw-items-start tw-gap-2 tw-inline-flex">
            <div className="tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
              {t("DashboardSettingWidgetDefaultDomain/key1")}
            </div>
          </div>
          <Form.Group className="tw-flex tw-items-center tw-gap-2">
            <Form.Check
              disabled={
                !permissions.settingsPage.widget.customization.poweredBy
              }
              defaultChecked={false}
              type="switch"
              id="poweredBy"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/removePoweredBy")}
            />
            <ProBadge className="tw-ms-2" />
          </Form.Group>
        </div>
      )}

      {permissions?.settingsPage?.widget?.customApiKey && (
        <ApiKeyConfiguration />
      )}
    </section>
  );
};

export default SettingTempPage;
